import { HD_CHIRON_DISPLAY_CONFIG } from './planet-grid/hd-planet-display-config';
import type { HdSimpleExtraPlanetVm } from './planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';

/** Zijde van een Chiron-positie (design of personality). */
export type HdChironSide = 'personality' | 'design';

/**
 * Rol van een Chiron-positie in het wond-verhaal (techniek van Genoa Bliven):
 * de natal personality-Chiron toont het thema van de wond, de natal
 * design-Chiron het werk om die te helen en de design-Chiron van de Chiron
 * return chart de resolutie.
 */
export type HdChironRole = 'wound' | 'work' | 'resolution';

/** Rol van een natal Chiron-positie: personality = wond, design = werk. */
export function hdChironNatalRole(side: HdChironSide): HdChironRole {
  return side === 'personality' ? 'wound' : 'work';
}

/**
 * Chiron-positie van een chart per zijde, uit `metadata.extraPlanets` van de
 * berekening. BELANGRIJK: géén chart-activatie: Chiron definieert geen poorten
 * of kanalen en telt nergens mee als activatie. Ze is enkel informatief
 * (simpele grid, statistieken, basis-paneel, gate-info).
 */
export interface HdChironPosition {
  readonly side: HdChironSide;
  readonly gate: number;
  readonly line: number;
  /** Kleur/toon/basis van de positie; 0 wanneer de berekening ze niet leverde. */
  readonly color: number;
  readonly tone: number;
  readonly base: number;
  /** Absolute ecliptische lengte in graden [0, 360); 0 wanneer onbekend. */
  readonly longitude: number;
}

/** Klik op een Chiron-item (grid, statistieken): opent de gate-info van haar poort. */
export interface HdChironClickEvent {
  readonly side: HdChironSide;
  readonly origin: { readonly x: number; readonly y: number };
}

function toFiniteNumber(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

/**
 * Chiron-positie van één zijde uit een `metadata`-blok (`extraPlanets`), of
 * `null` wanneer de chart geen (geldige) Chiron-data bevat.
 */
export function resolveHdChironPosition(
  metadata: Record<string, unknown> | null | undefined,
  side: HdChironSide,
): HdChironPosition | null {
  const extraPlanets = metadata?.['extraPlanets'];
  if (!Array.isArray(extraPlanets)) return null;
  const entry = (extraPlanets as Array<Record<string, unknown>>).find(
    candidate => candidate['planetId'] === HD_CHIRON_DISPLAY_CONFIG.planetId && candidate['side'] === side,
  );
  if (!entry) return null;
  const gate = toFiniteNumber(entry['gate']);
  const line = toFiniteNumber(entry['line']);
  if (gate < 1 || gate > 64 || line < 1 || line > 6) return null;
  return {
    side,
    gate,
    line,
    color: toFiniteNumber(entry['color']),
    tone: toFiniteNumber(entry['tone']),
    base: toFiniteNumber(entry['base']),
    longitude: toFiniteNumber(entry['longitude']),
  };
}

/** Chiron als extra cel van de planeetgrids (geen activatie); `null` zonder positie. */
export function toHdSimpleChironVm(position: HdChironPosition | null, tooltip: string): HdSimpleExtraPlanetVm | null {
  if (position == null) return null;
  return { glyph: HD_CHIRON_DISPLAY_CONFIG.astronomiconChar, gate: position.gate, line: position.line, tooltip };
}
